<template>
	<div class="directory-listing">

		<template v-if="!$root.userHasPermission({
			'igt': ['AM', 'CM', 'SA'],
			'operator': ['affiliate', 'customer', 'performance'],
		})">

			<div class="directory-listing__message">
				<p>In order to download assets, you need to be logged into an account.</p>
				<p>
					<router-link
						:to="{ name: 'Login' }"
						class="btn"
						@click.native="$root.$data.gameAsset = null"
					>
						<span>Login</span>
					</router-link>
					or
					<a
						class="link--styled"
						href="mailto:peter.nikashin@igt.com?subject=IGT Casino Lounge - Request Account"
					>Enquire about an account</a>
				</p>
			</div>

		</template>

		<template v-else>

			<directory-listing-nav
				v-if="hasNav"
				:navigation="navigation"
				:readOnly="readOnly"
				:has-download-btn="enableDownload"
				class="directory-listing__nav"
				@navigateToFolder="setNavigation"
				@newFolder="showNewFolder = true"
			/>

			<div class="directory-listing__header">
				<div class="grid grid--g20 grid--middle">
					<div class="grid__item grid__item--auto--phab grid__item--space--phab seven-tenths--not-phab">
						<span
							class="clickable"
							@click="switchSortBy('name')"
						>
							<span class="vm">Name</span>
							<span
								class="vm fs--7 fonticon"
								:class="{
									'fonticon--arrow--down': sort.name !== 'desc',
									'fonticon--arrow--up': sort.name === 'desc',
								}"
							/>
						</span>
					</div>
					<mq-layout
						:mq="['ltab', 'desk']"
						class="grid__item two-tenths hc"
					>
						<template v-if="!isRootDirectory || !hideItemCountAtRoot">
							No of Items
						</template>
					</mq-layout>
					<div class="grid__item grid__item--auto--phab one-tenth--not-phab hc">
						Action
					</div>
				</div>
			</div>

			<div
				v-for="item in currentDirectory.children"
				:key="item.id"
				:class="['directory-listing__item--' + item.type, {
					'directory-listing__item--has-thumb': item.type === 'folder' && item.iconOverride,
					'directory-listing__item--moving': assetIsMoving(item),
				}]"
				class="directory-listing__item"
			>
				<div class="grid grid--g20 grid--middle grid--nowrap">
					<div class="grid__item grid__item--auto--phab grid__item--space--phab seven-tenths--not-phab">

						<div class="grid grid--g16 grid--middle grid--nowrap">
							<div class="grid__item grid__item--auto">
								<div class="directory-listing__image-wrapper">
									<img
										v-if="item.type === 'folder' && !item.iconOverride"
										src="@/assets/img/icons/folder@2x.png"
										class="directory-listing__image clickable"
										@click.prevent="openFolder(item)"
									/>
									<img
										v-else-if="item.type=='folder' && item.iconOverride"
										:src="item.iconOverride"
										class="directory-listing__image clickable"
										@click.prevent="openFolder(item)"
									/>
									<img
										v-else-if="item.type=='image'"
										:src="item.link"
										class="directory-listing__image"
									/>
									<img
										v-else
										:src="$root.requireIcon(item.name, item.type)"
										class="directory-listing__image"
									/>
								</div>
							</div>
							<div class="grid__item grid__item--space">
								<span
									v-if="!item.renaming"
									:class="{
										'clickable' : item.type === 'folder'
									}"
									class="directory-listing__title link--wrap"
									@click.prevent="openFolder(item)"
								>
									{{ item.name }}
								</span>
								<div
									v-else
									class="grid grid--nowrap grid--middle grid--b8"
								>
									<div class="grid__item grid__item--space">
										<text-field
											v-model="item.newName"
											type="text"
											name="rename"
										/>
									</div>
									<div class="grid__item grid__item--auto">
										<span
											class="directory-listing__icon fonticon fonticon--plus--circle fc-pal--blue"
											@click.stop="renameAsset(item, item.newName)"
										/>
									</div>
									<div class="grid__item grid__item--auto">
										<span
											class="directory-listing__icon fonticon fonticon--close--circle fc-pal--red"
											@click.stop="item.renaming = false"
										/>
									</div>
								</div>
							</div>
						</div>

					</div>
					<mq-layout
						:mq="['ltab', 'desk']"
						class="grid__item two-tenths"
					>
						<div
							v-if="!isRootDirectory || !hideItemCountAtRoot"
							class="directory-listing__meta"
						>
							<template v-if="!item.hasChildren">{{ item.type == 'folder' ? '0 items' : '-' }}</template>
							<template v-else-if="!item.children">
								<img src="@/assets/img/loading.gif"/>
							</template>
							<template v-else>
								{{ item.children.length }} item{{ item.children.length > 1 ? 's' : '' }}
							</template>
						</div>
					</mq-layout>
					<div class="grid__item grid__item--auto--phab one-tenth--not-phab hc">
						<span
							v-if="assetIsMoving(item)"
							class="fonticon fonticon--close--circle clickable  fc-pal--red fs--32c"
							@click.stop="$store.commit('admin/removeMovingAsset', item)"
						/>
						<action-tooltip v-else-if="!readOnly">
							<span
								class="directory-listing__action"
								@click.stop="downloadAsset(item)"
							>
								<span class="directory-listing__download">Download</span>
							</span>
							<span
								class="directory-listing__action"
								@click.stop="$set(item, 'renaming', true); $set(item, 'newName', removeExtension(item.name));"
							>
								<span class="directory-listing__rename">Rename</span>
							</span>
							<span
								class="directory-listing__action"
								@click.stop="moveAsset(item)"
							>
								<span class="directory-listing__rename">Move</span>
							</span>
							<span
								class="directory-listing__action"
								@click.stop="deleteAsset(item)"
							>
								<span class="directory-listing__delete">Delete</span>
							</span>
						</action-tooltip>
						<span
							v-else-if="enableDownload"
							class="fonticon fonticon--download clickable fc-pal--link fs--20c"
							@click.stop="downloadAsset(item)"
						/>
						<span
							v-else
							class="link--action link--colored"
							@click.stop="$emit('selected', item)"
						>Select</span>
					</div>
				</div>
			</div>

			<div
				v-if="movingAssets && movingAssets.length > 0"
				class="directory-listing__move"
				@click="pasteMovingAssets"
			>
				<div class="directory-listing__move-item">
					Move {{ movingAssets.length }} item{{ movingAssets.length == 1 ? '' : 's' }} here
				</div>
			</div>


			<expose
				v-if="!readOnly"
				:active="showNewFolder === true"
				class="directory-listing__new-folder"
				@expose-close="showNewFolder = false"
			>
				<form
					class="directory-listing__new-folder-container"
					@submit.prevent.stop="createNewFolder(currentDirectory.id, newFolderName)"
				>
					<label-field
						class="field__label"
						value="Folder name"
					/>
					<div class="grid grid--g16 grid--auto grid--nowrap grid--middle">
						<div class="grid__item grid__item--space">
							<text-field
								v-model="newFolderName"
								ref="newFolder"
								class="w--100p"
								type="text"
								name="new-folder"
								required
							/>
							<div
								v-if="!folderNameValid"
								class="mt--8 fc-pal--form-error fs--14"
							>Invalid folder name</div>
						</div>
						<div class="grid__item">
							<submit-field>
								<span>Create</span>
							</submit-field>
						</div>
					</div>
				</form>
			</expose>

		</template>

	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { map, forEach, find, findIndex, orderBy, cloneDeep } from 'lodash'

import LabelField from '../atoms/LabelField'
import TextField from '../atoms/TextField'
import SubmitField from '../atoms/SubmitField'
import ActionTooltip from '../molecules/ActionTooltip'
import DirectoryListingNav from '../molecules/DirectoryListingNav'
import Expose from '../molecules/Expose'

export default {
	name: 'DirectoryListing',
	components: {
		LabelField,
		TextField,
		SubmitField,
		ActionTooltip,
		DirectoryListingNav,
		Expose,
	},
	data () {
		return {
			internalDirectory: null,
			currentDirectory: [],
			navigation: [],
			sort: {
				name: 'asc'
			},
			showNewFolder: false,
			newFolderName: null,
			folderNameValid: true,
		}
	},
	emits: ['selected'],
	props: {
		directory: Object,
		hasNav: {
			type: Boolean,
			default: true
		},
		readOnly: {
			type: Boolean,
			default: true
		},
		rootName: {
			type: String,
			default: 'Root'
		},
		hideItemCountAtRoot: {
			type: Boolean,
			default: false,
		},
		preselect: [String, Number],
		enableDownload: {
			type: Boolean,
			default: true
		}
	},
	computed: {
		...mapGetters({
			movingAssets: 'admin/movingAssets',
		}),
		isRootDirectory: function () {
			return this.navigation && this.navigation.length == 1
		},
	},
	watch: {
		navigation: function (newValue, oldValue) {
			var self = this
			self.$emit('navigationChanged', newValue)
		},
		directory: {
			handler: function (newValue, oldValue) {
				var self = this
				self.internalDirectory = cloneDeep(newValue)
				self.refreshNavigationData()
			},
			deep: true
		},
		currentDirectory: function (newValue, oldValue) {
			var self = this
			self.$emit('navigatedTo', newValue)
		},
		showNewFolder: function (newValue, oldValue) {
			this.$nextTick(() => {
				if (newValue && this.$refs.newFolder) {
					setTimeout(() => {
						this.$refs.newFolder.$refs.fieldElement.focus()
					}, 100)
				}
			})
		},
	},
	created: function () {
	},
	mounted: function () {
		var self = this
		self.internalDirectory = cloneDeep(self.directory)
		self.refreshNavigationData()
		self.setCurrentDirectory(self.internalDirectory)

		if (this.preselect) {
			var child = find(this.internalDirectory.children, { id: parseInt(this.preselect) })
			if (child && child.type == 'folder') {
				this.openFolder(child)
			}
		}
	},
	methods: {
		setNavigation (navigation) {
			var self = this
			self.navigation = navigation
			self.setCurrentDirectory(navigation[navigation.length - 1])

			self.$emit('navigationChanged', navigation)
		},
		refreshNavigationData () {
			var self = this

			// Assume we have existing navigation that needs rebuilding
			var navigation = []
			navigation.push({
				name: self.rootName,
				id: self.internalDirectory.id,
				hasChildren: true,
				children: self.internalDirectory.children
			})

			if (self.navigation.length > 1) {
				var currentFolder = self.internalDirectory
				for (var i = 1; i < self.navigation.length; i++) {
					if (!currentFolder.hasChildren) { break }
					var index = findIndex(currentFolder.children, { id: self.navigation[i].id })
					if (index == -1) { break }
					navigation.push(currentFolder.children[index])
					currentFolder = currentFolder.children[index]
				}
			}

			self.navigation = navigation
			self.setCurrentDirectory(self.navigation[self.navigation.length - 1])

		},
		openFolder: function (item) {
			var self = this

			if (item.type !== 'folder' || item.renaming === true) {
				return
			}

			self.navigation.push(item)
			self.setCurrentDirectory(item)
		},
		downloadAsset: function (item) {
			var self = this
			var id = self.isPlainObject(item) && item.hasOwnProperty('id') ? item.id : item

			self.$api.getAsset(id).then((success) => {
				if (success.data.isZip) {
					window.location.assign(self.$apiPath + '/download/asset/zip/' + parseInt(success.data.id, 10))
				} else {
					window.location.assign(self.$apiPath + '/download/asset/' + parseInt(success.data.id, 10), '_blank')
				}
			}).catch((error) => {
				self.$alert.danger({ message: error.message })
			})
		},
		renameAsset: function (item, newName) {
			var self = this

			self.$api.renameAsset(item.id, newName).then((success) => {
				self.$emit('input')
			}).catch((error) => {
				self.$alert.danger({ message: error.message })
			})
		},
		moveAsset: function (item) {
			this.$store.commit('admin/addMovingAsset', item)
		},
		deleteAsset: function (item) {
			var self = this

			if (confirm('Are you sure you want to delete \'' + item.name + '\'?')) {
				self.$api.deleteAsset(item.id).then((success) => {
					this.$store.commit('admin/removeMovingAsset', item)
					self.$emit('input')
				}).catch((error) => {
					self.$alert.danger({ message: error.message })
				})
			}
		},
		createNewFolder: function (parentId, name) {
			this.folderNameValid = true

			if (!/^[^\\/?%*:|"<>\.]+$/.test(name)) {
				this.folderNameValid = false
				return
			}

			this.$api.newFolder(parentId, name).then((success) => {
				this.$emit('input')
			}).catch((error) => {
				this.$alert.danger({ message: error.message })
			})
				.finally(() => {
					this.showNewFolder = false
					this.newFolderName = null
				})
		},
		assetIsMoving: function (item) {
			if (!this.movingAssets || this.movingAssets.length === 0) { return false }
			return findIndex(this.movingAssets, { id: item.id }) !== -1
		},
		pasteMovingAssets: function () {
			if (!this.movingAssets || this.movingAssets.length === 0) { return }

			// All pasting into the same parent?
			var moveableAssets = []

			forEach(this.movingAssets, movingAsset => {
				var isInCurrentDirectory = find(this.currentDirectory.children, { id: movingAsset.id })
				if (!isInCurrentDirectory) {
					moveableAssets.push(movingAsset)
				}
			})

			if (moveableAssets.length == 0) {
				this.$store.commit('admin/movingAssets', [])
				return
			}

			// Pasting into a new parent
			this.$api.moveAssets(map(moveableAssets, 'id'), this.currentDirectory.id)
				.then((success) => {
					this.currentDirectory.hasChildren = true
					forEach(moveableAssets, movingAsset => {
						this.currentDirectory.children.push(movingAsset)
					})
					this.sortDirectory(this.currentDirectory)
					this.$store.commit('admin/movingAssets', [])
					this.$emit('input')
				})
				.catch((error) => {
					this.$alert.danger({ message: error.message })
				})
		},
		removeExtension: function (filename) {
			return filename.indexOf('.') !== -1 ? filename.split('.').slice(0, -1).join('.') : filename
		},
		setCurrentDirectory (directory) {
			var self = this
			self.currentDirectory = directory

			if (directory.hasChildren) {
				if (!directory.children) {
					self.getDirectoryChildren(directory)
						.then(success => {
							self.sortDirectory(directory)
						})
				} else {
					self.sortDirectory(directory)

					if (!this.isRootDirectory || !this.hideItemCountAtRoot) {
						forEach(directory.children, function (childDirectory) {
							if (childDirectory.hasChildren && !childDirectory.children) {
								self.getDirectoryChildren(childDirectory)
							}
						})
					}
				}
			}
		},
		getDirectoryChildren: function (directory) {
			var self = this

			var request = self.$api.getAssetDirectory(directory.id)
			request.then((success) => {
				if (success.hasOwnProperty('data') && success.data.length == 1 && success.data[0].children) {
					directory.children = success.data[0].children
					self.$forceUpdate()
				}
			}).catch((error) => {
				console.log(error)
			})

			return request
		},
		switchSortBy: function (key) {
			var self = this

			self.sort[key] = self.sort[key] === 'desc' ? 'asc' : 'desc'
			self.sortDirectory(self.currentDirectory)
		},
		sortDirectory: function (directory) {
			if (directory.children) {
				directory.children = orderBy(directory.children, Object.keys(this.sort), Object.values(this.sort))
			}
		},
	}
}
</script>

<style lang="scss">
@import "src/scss/toolkit";

$c: ".directory-listing";
#{$c} {
	&__message {
		text-align: center;
	}

	&__nav {
		margin-bottom: rem(30);
	}

	&__header {
		@include extend(tabular-layout__header);
	}

	&__item {
		@include extend(tabular-layout__item);

		&--has-thumb {
			padding: rem(8) rem(4);
		}
	}

	&__image-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		width: rem(32);
		height: rem(32);

		#{$c}__item--folder & {
			cursor: pointer;
		}

		#{$c}__item--has-thumb & {
			width: rem(77);
			height: rem(56);
		}

		#{$c}__item--moving & {
			opacity: 0.6;
		}
	}

	&__image {
		max-width: 100%;
		max-height: 100%;
	}

	&__title {
		@include css-variables {
			transition: color calc(1s * #{var(base-trans-speed)});
		}

		#{$c}__item:hover & {
			color: palette(blue);
		}

		#{$c}__item--folder & {
			cursor: pointer;
		}

		#{$c}__item--moving & {
			opacity: 0.6;
		}
	}

	&__meta {
		text-align: center;
		color: palette('grey');

		#{$c}__item--moving & {
			opacity: 0.6;
		}
	}

	&__icon {
		cursor: pointer;
		font-size: rem(32);
	}

	&__action {
		cursor: pointer;
	}

	&__move {
		@include extend(lato);
		font-size: rem(14);
		text-align: center;
		margin-top: rem(16);
		padding: rem(16);
		color: palette('blue');
		border: 1px dashed;
		background-color: lighten(palette(blue, base, false), 48);
		cursor: pointer;
	}

	&__new-folder {
		background-color: transparentize(palette(black, base, true), 0.15) !important;

		&-container {
			@include extend(bgc-pal--white);
			padding: rem(48);
			box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
			width: 432px;
			max-width: 100%;
		}
	}
}

</style>



// WEBPACK FOOTER //
// src/components/organisms/DirectoryListing.vue